import React, { useState, useMemo, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Statistic,
  Typography,
  Select,
  Space,
  Progress,
  Tag,
  Table,
  Tabs,
  Alert,
  Spin,
  notification,
  Button,
  Badge,
} from "antd";
import {
  UserOutlined,
  BookOutlined,
  PercentageOutlined,
  TrophyOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  ExportOutlined,
} from "@ant-design/icons";

import getDataInformeEspiga from "../../services/espiga/espigaServices";
import { useSelector } from "react-redux";
import ChartCard from "../charts/ChartCard";
import * as XLSX from "xlsx";

const { Title, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

const COLORS = [
  "#0B80C4",
  "#4CAF50",
  "#FF9800",
  "#F44336",
  "#9C27B0",
  "#607D8B",
  "#795548",
  "#009688",
  "#E91E63",
  "#673AB7",
  "#3F51B5",
  "#2196F3",
  "#00BCD4",
  "#4CAF50",
  "#8BC34A",
  "#CDDC39",
  "#FFEB3B",
  "#FFC107",
  "#FF9800",
  "#FF5722",
];

const EspigaDashboard = () => {
  const [data, setData] = useState([]);
  const [dataOriginal, setDataOriginal] = useState([]);
  const [dataFilter, setDataFilter] = useState([]);
  const [listDepartamento, setListDepartamento] = useState([]);
  const [listCodigoSir, setlistCodigoSir] = useState([]);
  const [listMunicipiosPorDepartamento, setListMunicipiosPorDepartamento] =
    useState({});
  const [loading, setLoading] = useState(true);
  const [selectedDepartment, setSelectedDepartment] = useState("todos");
  const [selectedCodigoSir, setSelectedCodigoSir] = useState("todos");
  const [selectedModule, setSelectedModule] = useState("todos");
  const [selectedMunicipio, setSelectedMunicipio] = useState("todos");
  const usernameFromState = useSelector((state) => state.auth.user);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await getDataInformeEspiga();
      const { status, data: responseData } = response;

      if (status) {
        setData(responseData);
        setDataOriginal(responseData);
        setDataFilter(responseData);
      } else {
        notification.error({ message: response.message });
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "An error occurred while fetching data.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const exportarExcel = () => {
    let columnsExport = [
      { title: "Código", dataIndex: "codigo_sir" },
      { title: "Nombre del participante", dataIndex: "nombre_participante" },
      { title: "Genero", dataIndex: "genero" },
      { title: "Departamento", dataIndex: "departamento" },
      { title: "Municipio", dataIndex: "municipio" },
      { title: "Modulo", dataIndex: "nombre_modulo" },
      { title: "Total estaciones", dataIndex: "total_estaciones_modulo" },
      { title: "Estaciones completadas", dataIndex: "estaciones_completadas" },
      { title: "Porcentaje", dataIndex: "porcentaje_avance" },
    ];

    const ws = XLSX.utils.json_to_sheet(
      dataOriginal.map((row) => {
        const obj = {};
        columnsExport.forEach((col) => {
          obj[col.title] = row[col.dataIndex];
        });
        return obj;
      }),
      { origin: "A2" } // Dejamos la fila 1 para cabeceras
    );

    // Agregar manualmente cabeceras en la fila 1
    const headerRow = columnsExport.map((col) => col.title);
    XLSX.utils.sheet_add_aoa(ws, [headerRow], { origin: "A1" });

    // Aplicar estilos a cabeceras (fila 1)
    headerRow.forEach((_, idx) => {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: idx });
      if (!ws[cellRef]) return;
      ws[cellRef].s = {
        fill: {
          fgColor: { rgb: "FFC000" }, // Color de fondo (amarillo Excel)
        },
        font: {
          bold: true,
          color: { rgb: "FFFFFF" }, // Texto blanco
        },
        alignment: {
          horizontal: "center",
          vertical: "center",
        },
      };
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Informe Espiga");
    XLSX.writeFile(wb, "informe_espiga_general.xlsx");
  };

  const exportarExcelDetallado = () => {
    let columnsExport = [
      { title: "Código", dataIndex: "codigo_sir" },
      { title: "Nombre del participante", dataIndex: "nombre_participante" },
      { title: "Genero", dataIndex: "genero" },
      { title: "Departamento", dataIndex: "departamento" },
      { title: "Municipio", dataIndex: "municipio" },
      { title: "Módulos", dataIndex: "modulosInscritos" },
      { title: "Total estaciones", dataIndex: "totalEstacionesUser" },
      { title: "Estaciones completadas", dataIndex: "completadasUser" },
      { title: "Avance", dataIndex: "riesgo" },
      { title: "Porcentaje", dataIndex: "avanceUser" },
    ];

    // Crear datos sin cabeceras
    const data = processedData.participantesConRiesgo.map((row) => {
      const obj = {};
      columnsExport.forEach((col) => {
        obj[col.title] = row[col.dataIndex];
      });
      return obj;
    });

    // Crear hoja vacía
    const ws = XLSX.utils.aoa_to_sheet([]);

    // Agregar cabeceras en la fila 1
    const headerRow = columnsExport.map((col) => col.title);
    XLSX.utils.sheet_add_aoa(ws, [headerRow], { origin: "A1" });

    // Agregar datos desde la fila 2
    XLSX.utils.sheet_add_json(ws, data, { skipHeader: true, origin: "A2" });

    // Dar estilo a las cabeceras
    headerRow.forEach((_, idx) => {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: idx });
      if (!ws[cellRef]) return;
      ws[cellRef].s = {
        font: { bold: true, sz: 12 },
        alignment: { horizontal: "center", vertical: "center" },
      };
    });

    // Guardar archivo
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Informe Espiga");
    XLSX.writeFile(wb, "informe_detallado_espiga.xlsx");
  };

  const processedData = useMemo(() => {
    if (!data || data.length === 0) return {};

    let filteredData = data;

    // Aplicar filtros en cascada
    if (selectedDepartment !== "todos") {
      filteredData = filteredData.filter(
        (item) => item.departamento === selectedDepartment
      );
    }

    if (selectedMunicipio !== "todos") {
      filteredData = filteredData.filter(
        (item) => item.municipio === selectedMunicipio
      );
    }

    if (selectedCodigoSir !== "todos") {
      filteredData = filteredData.filter(
        (item) => item.codigo_sir === selectedCodigoSir
      );
    }

    if (selectedModule !== "todos") {
      filteredData = filteredData.filter(
        (item) => item.nombre_modulo === selectedModule
      );
    }

    // 🔹 Listas dependientes dinámicas
    const departamentos = [...new Set(filteredData.map((p) => p.departamento))];

    let departamentosFiltrados = departamentos;

    if (usernameFromState?.departamento?.length > 0) {
      const departamentosUsuario = usernameFromState.departamento.map(
        (d) => d.departamento
      );

      departamentosFiltrados = departamentos.filter((dep) =>
        departamentosUsuario.includes(dep)
      );
    }

    setListDepartamento(departamentosFiltrados);

    // Agrupar municipios por departamento
    const municipiosPorDepartamento = filteredData.reduce((acc, p) => {
      if (!acc[p.departamento]) {
        acc[p.departamento] = new Set();
      }
      acc[p.departamento].add(p.municipio);
      return acc;
    }, {});
    // Lista de codigos_sir dependiente de departamentos filtrados
    const codigo_sir = [
      ...new Set(
        filteredData
          .filter((p) => departamentosFiltrados.includes(p.departamento))
          .map((p) => p.codigo_sir)
      ),
    ];

    setlistCodigoSir(codigo_sir);
    setListMunicipiosPorDepartamento(municipiosPorDepartamento);

    // 🔹 Métricas generales
    const totalParticipantes = new Set(
      filteredData.map((item) => item.id_usuario)
    ).size;

    const totalModulos = new Set(filteredData.map((item) => item.nombre_modulo))
      .size;

    const totalEstaciones = filteredData.reduce(
      (sum, item) => sum + parseInt(item.estaciones_completadas || 0),
      0
    );

    const totalEstacionesPosibles = filteredData.reduce(
      (sum, item) => sum + parseInt(item.total_estaciones_modulo || 0),
      0
    );

    const promedioAvance =
      totalEstacionesPosibles > 0
        ? (totalEstaciones / totalEstacionesPosibles) * 100
        : 0;

    // 🔹 Usuarios únicos con sus datos
    const usuarios = Array.from(
      new Set(filteredData.map((item) => item.id_usuario))
    ).map((userId) => {
      const userRecords = filteredData.filter(
        (item) => item.id_usuario === userId
      );
      const firstRecord = userRecords[0];
      const totalEstacionesUser = userRecords.reduce(
        (sum, record) => sum + parseInt(record.total_estaciones_modulo || 0),
        0
      );
      const completadasUser = userRecords.reduce(
        (sum, record) => sum + parseInt(record.estaciones_completadas || 0),
        0
      );
      const avanceUser =
        totalEstacionesUser > 0
          ? (completadasUser / totalEstacionesUser) * 100
          : 0;

      return {
        ...firstRecord,
        totalEstacionesUser,
        completadasUser,
        avanceUser,
        modulosInscritos: userRecords.length,
      };
    });

    // 🔹 Distribución por género
    const pieGeneroData = usuarios.reduce((acc, user) => {
      acc[user.genero] = (acc[user.genero] || 0) + 1;
      return acc;
    }, {});

    // 🔹 Rangos de avance
    const rangosAvance = {
      "Sin avance (0%)": 0,
      "Bajo (1-25%)": 0,
      "Medio (26-50%)": 0,
      "Alto (51-75%)": 0,
      "Completo (76-100%)": 0,
    };

    usuarios.forEach((user) => {
      if (user.avanceUser === 0) rangosAvance["Sin avance (0%)"]++;
      else if (user.avanceUser <= 25) rangosAvance["Bajo (1-25%)"]++;
      else if (user.avanceUser <= 50) rangosAvance["Medio (26-50%)"]++;
      else if (user.avanceUser <= 75) rangosAvance["Alto (51-75%)"]++;
      else rangosAvance["Completo (76-100%)"]++;
    });

    const pieRangosData = rangosAvance;

    // 🔹 Progreso por módulo
    const moduloStats = filteredData.reduce((acc, item) => {
      if (!acc[item.nombre_modulo]) {
        acc[item.nombre_modulo] = {
          totalEstaciones: 0,
          estacionesCompletadas: 0,
          participantes: new Set(),
        };
      }
      acc[item.nombre_modulo].totalEstaciones += parseInt(
        item.total_estaciones_modulo || 0
      );
      acc[item.nombre_modulo].estacionesCompletadas += parseInt(
        item.estaciones_completadas || 0
      );
      acc[item.nombre_modulo].participantes.add(item.id_usuario);
      return acc;
    }, {});

    const barModuloData = Object.entries(moduloStats).map(
      ([modulo, stats]) => ({
        name: modulo,
        completadas: stats.estacionesCompletadas,
        total: stats.totalEstaciones,
        participantes: stats.participantes.size,
        porcentaje:
          stats.totalEstaciones > 0
            ? (
                (stats.estacionesCompletadas / stats.totalEstaciones) *
                100
              ).toFixed(1)
            : 0,
        pendientes: stats.totalEstaciones - stats.estacionesCompletadas,
      })
    );

    // 🔹 Datos por departamento
    const deptStats = usuarios.reduce((acc, user) => {
      if (!acc[user.departamento]) {
        acc[user.departamento] = {
          participantes: 0,
          totalEstaciones: 0,
          completadas: 0,
          municipios: new Set(),
          hombres: 0,
          mujeres: 0,
        };
      }
      acc[user.departamento].participantes += 1;
      acc[user.departamento].totalEstaciones += user.totalEstacionesUser;
      acc[user.departamento].completadas += user.completadasUser;
      acc[user.departamento].municipios.add(user.municipio);
      if (user.genero === "Hombre") acc[user.departamento].hombres += 1;
      else acc[user.departamento].mujeres += 1;
      return acc;
    }, {});

    const barDepartamentoData = Object.entries(deptStats).map(
      ([dept, stats]) => ({
        name: dept,
        participantes: stats.participantes,
        completadas: stats.completadas,
        total: stats.totalEstaciones,
        avance:
          stats.totalEstaciones > 0
            ? ((stats.completadas / stats.totalEstaciones) * 100).toFixed(1)
            : 0,
        municipios: stats.municipios.size,
        hombres: stats.hombres,
        mujeres: stats.mujeres,
      })
    );

    // 🔹 Participantes con riesgo
    const participantesConRiesgo = usuarios.map((user) => ({
      ...user,
      riesgo:
        user.avanceUser === 0
          ? "Sin avance"
          : user.avanceUser < 25
          ? "Bajo"
          : user.avanceUser < 50
          ? "Medio"
          : "Alto",
    }));

    // 🔹 Alertas
    const alertas = [];
    const participantesSinAvance = usuarios.filter(
      (u) => u.avanceUser === 0
    ).length;
    const participantesBajoAvance = usuarios.filter(
      (u) => u.avanceUser > 0 && u.avanceUser < 25
    ).length;
    const participantesCompletos = usuarios.filter(
      (u) => u.avanceUser >= 100
    ).length;

    if (participantesSinAvance > 0) {
      alertas.push({
        type: "error",
        message: `${participantesSinAvance} participantes sin avance registrado`,
      });
    }
    if (participantesBajoAvance > 0) {
      alertas.push({
        type: "warning",
        message: `${participantesBajoAvance} participantes con avance menor al 25%`,
      });
    }
    if (participantesCompletos > 0) {
      alertas.push({
        type: "success",
        message: `¡${participantesCompletos} participantes han completado todos sus módulos!`,
      });
    }

    return {
      totalParticipantes,
      totalModulos,
      totalEstaciones,
      totalEstacionesPosibles,
      promedioAvance: Math.round(promedioAvance),
      pieGeneroData,
      pieRangosData,
      barModuloData,
      barDepartamentoData,
      participantesConRiesgo,
      alertas,
      usuarios,
    };
  }, [
    data,
    selectedDepartment,
    selectedModule,
    selectedMunicipio,
    selectedCodigoSir,
  ]);

  const modulos = data
    ? [...new Set(data.map((item) => item.nombre_modulo))]
    : [];
  const municipiosDisponibles =
    selectedDepartment !== "todos" &&
    listMunicipiosPorDepartamento[selectedDepartment]
      ? Array.from(listMunicipiosPorDepartamento[selectedDepartment])
      : [];

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          background: "linear-gradient(135deg, #eef0f7ff 0%, #e7e0eeff 100%)",
        }}
      >
        <Card style={{ textAlign: "center", padding: "40px" }}>
          <Spin size="large" />
          <div style={{ marginTop: "16px" }}>
            <Text>Cargando datos del informe...</Text>
          </div>
        </Card>
      </div>
    );
  }

  if (!processedData.totalParticipantes) {
    return (
      <div
        style={{
          padding: "40px",
          background: "linear-gradient(135deg, #eef0f7ff 0%, #e7e0eeff 100%)",
          minHeight: "100vh",
        }}
      >
        <Card style={{ textAlign: "center", padding: "40px" }}>
          <WarningOutlined
            style={{ fontSize: "48px", color: "#faad14", marginBottom: "16px" }}
          />
          <Title level={3}>No hay datos disponibles</Title>
          <Text>No se encontraron registros para mostrar en el informe.</Text>
        </Card>
      </div>
    );
  }

  // Columnas para la tabla de participantes con análisis de riesgo
  const participantesColumns = [
    {
      title: "Participante",
      dataIndex: "nombre_participante",
      key: "nombre_participante",
      width: 200,
      render: (text) => (
        <Text strong style={{ fontSize: "12px" }}>
          {text}
        </Text>
      ),
      fixed: "left",
    },
    {
      title: "Avance",
      dataIndex: "riesgo",
      key: "riesgo",
      width: 80,
      render: (riesgo) => {
        const colors = {
          "Sin avance": "red",
          Bajo: "orange",
          Medio: "yellow",
          Alto: "green",
        };
        return <Tag color={colors[riesgo]}>{riesgo}</Tag>;
      },
      filters: [
        { text: "Alto", value: "Alto" },
        { text: "Medio", value: "Medio" },
        { text: "Bajo", value: "Bajo" },
        { text: "Sin avance", value: "Sin avance" },
      ],
      onFilter: (value, record) => record.riesgo === value,
    },
    {
      title: "Género",
      dataIndex: "genero",
      key: "genero",
      width: 80,
      render: (genero) => (
        <Tag color={genero === "Mujer" ? "pink" : "blue"}>{genero}</Tag>
      ),
    },
    {
      title: "Departamento",
      dataIndex: "departamento",
      key: "departamento",
      width: 120,
    },
    {
      title: "Municipio",
      dataIndex: "municipio",
      key: "municipio",
      width: 120,
    },
    {
      title: "Módulos",
      dataIndex: "modulosInscritos",
      key: "modulosInscritos",
      width: 80,
      align: "center",
      sorter: (a, b) => a.modulosInscritos - b.modulosInscritos,
    },
    {
      title: "Completadas",
      dataIndex: "completadasUser",
      key: "completadasUser",
      width: 100,
      align: "center",
      render: (completadas, record) =>
        `${completadas}/${record.totalEstacionesUser}`,
      sorter: (a, b) => a.completadasUser - b.completadasUser,
    },

    {
      title: "Porcentaje",
      dataIndex: "avanceUser",
      key: "avanceUser",
      width: 150,
      render: (avance) => (
        <Progress
          percent={Math.round(avance)}
          size="small"
          strokeColor={
            avance >= 75
              ? "#4CAF50"
              : avance >= 50
              ? "#FF9800"
              : avance >= 25
              ? "#FFC107"
              : "#F44336"
          }
        />
      ),
      sorter: (a, b) => a.avanceUser - b.avanceUser,
    },
  ];

  return (
    <Card style={{ padding: 20 }}>
      {/* Header */}
      <div
        style={{
          textAlign: "center",
          marginBottom: "32px",
          background: "rgba(255, 255, 255, 0.95)",
          padding: "24px",
          borderRadius: "16px",
          backdropFilter: "blur(10px)",
        }}
      >
        <Title
          level={1}
          style={{
            color: "#0B3360",
            marginBottom: "8px",
            fontSize: "2.5rem",
            fontWeight: "700",
          }}
        >
          📊 INFORME INTEGRAL DE SEGUIMIENTO Y MONITOREO
        </Title>
        <Title
          level={2}
          style={{
            color: "#4A90E2",
            marginBottom: "16px",
            fontSize: "1.8rem",
            fontWeight: "500",
          }}
        >
          APP ESPIGA - ANÁLISIS AVANZADO
        </Title>
        <Space>
          <Button
            type="primary"
            onClick={exportarExcel}
            icon={<ExportOutlined />}
          >
            Exportar XLSX
          </Button>
        </Space>
      </div>

      {/* Alertas */}
      {processedData.alertas && processedData.alertas.length > 0 && (
        <Row gutter={[24, 16]} style={{ marginBottom: "24px" }}>
          {processedData.alertas.map((alerta, index) => (
            <Col key={index} xs={24} lg={6}>
              <Alert
                message={alerta.message}
                type={alerta.type}
                icon={alerta.icon}
                showIcon
                style={{ borderRadius: "12px" }}
              />
            </Col>
          ))}
        </Row>
      )}

      {/* Filtros */}
      <Card
        style={{
          marginBottom: "24px",
          borderRadius: "16px",
          background: "rgba(255, 255, 255, 0.9)",
          backdropFilter: "blur(10px)",
        }}
      >
        <Title level={4} style={{ color: "#0B3360", marginBottom: "16px" }}>
          🔍 Filtros de Análisis
        </Title>
        <Space size="large" wrap>
          <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
            <Col xs={24} sm={5} md={5}>
              <Select
                style={{ width: 200, marginLeft: 8 }}
                showSearch
                optionFilterProp="children" // busca dentro del texto de las opciones
                value={selectedDepartment}
                onChange={(value) => {
                  setSelectedDepartment(value);
                  setSelectedMunicipio("todos");
                }}
              >
                <Option value="todos">Todos los departamentos</Option>
                {listDepartamento.map((dept) => (
                  <Option key={dept} value={dept}>
                    {dept}
                  </Option>
                ))}
              </Select>
            </Col>

            <Col xs={24} sm={5} md={5}>
              <Select
                style={{ width: 200, marginLeft: 8 }}
                showSearch
                optionFilterProp="children" // busca dentro del texto de las opciones
                value={selectedMunicipio}
                onChange={setSelectedMunicipio}
                disabled={selectedDepartment === "todos"}
              >
                <Option value="todos">Todos los municipios</Option>
                {municipiosDisponibles.map((municipio) => (
                  <Option key={municipio} value={municipio}>
                    {municipio}
                  </Option>
                ))}
              </Select>
            </Col>

            <Col xs={24} sm={5} md={5}>
              <Select
                style={{ width: 200, marginLeft: 8 }}
                showSearch
                optionFilterProp="children" // busca dentro del texto de las opciones
                value={selectedModule}
                onChange={setSelectedModule}
              >
                <Option value="todos">Todos los módulos</Option>
                {modulos.map((modulo) => (
                  <Option key={modulo} value={modulo}>
                    {modulo}
                  </Option>
                ))}
              </Select>
            </Col>

            <Col xs={24} sm={5} md={5}>
              <Select
                showSearch
                style={{ width: 200, marginLeft: 8 }}
                        value={selectedCodigoSir}
                placeholder="Selecciona un código SIR"
                onChange={setSelectedCodigoSir}
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.children).toLowerCase().includes(input.toLowerCase())
                }
              >
                <Option value="todos">Todos los códigos SIR</Option>
                {listCodigoSir.map((c) => (
                  <Option key={c} value={c}>
                    {c}
                  </Option>
                ))}
              </Select>
            </Col>

            <Col xs={24} sm={4} md={4}>
              <Button
                style={{ width: 50, marginLeft: 8 }}
                type="primary"
                icon={<ReloadOutlined />}
                onClick={() => {
                  setSelectedDepartment("todos");
                  setSelectedMunicipio("todos");
                  setSelectedCodigoSir("todos");
                  setSelectedModule("todos");
                }}
              ></Button>
            </Col>
          </Row>
        </Space>
      </Card>

      {/* KPIs principales expandidos */}
      <Row gutter={[24, 24]} style={{ margin: "20px 40px" }}>
        <Col xs={24} sm={12} lg={6}>
          <Card
            style={{
              textAlign: "center",
              borderRadius: "16px",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              border: "none",
              minHeight: "120px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Statistic
              title={
                <span style={{ color: "#fff", fontSize: "14px" }}>
                  Total Participantes
                </span>
              }
              value={processedData.totalParticipantes}
              prefix={<UserOutlined style={{ color: "#fff" }} />}
              valueStyle={{
                color: "#fff",
                fontSize: "2rem",
                fontWeight: "bold",
              }}
            />
            <div style={{ marginTop: "8px" }}>
              <Text style={{ color: "#fff", fontSize: "12px" }}>
                {processedData.usuarios?.filter((u) => u.avanceUser > 0)
                  .length || 0}{" "}
                activos
              </Text>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card
            style={{
              textAlign: "center",
              borderRadius: "16px",
              background: "linear-gradient(135deg, #4CAF50 0%, #45a049 100%)",
              border: "none",
              minHeight: "120px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Statistic
              title={
                <span style={{ color: "#fff", fontSize: "14px" }}>
                  Módulos Activos
                </span>
              }
              value={processedData.totalModulos}
              prefix={<BookOutlined style={{ color: "#fff" }} />}
              valueStyle={{
                color: "#fff",
                fontSize: "2rem",
                fontWeight: "bold",
              }}
            />
            <div style={{ marginTop: "8px" }}>
              <Text style={{ color: "#fff", fontSize: "12px" }}>
                {processedData.barModuloData?.filter(
                  (m) => parseFloat(m.porcentaje) > 50
                ).length || 0}{" "}
                con buen rendimiento
              </Text>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card
            style={{
              textAlign: "center",
              borderRadius: "16px",
              background: "linear-gradient(135deg, #FF9800 0%, #F57C00 100%)",
              border: "none",
              minHeight: "120px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Statistic
              title={
                <span style={{ color: "#fff", fontSize: "14px" }}>
                  Estaciones Completadas
                </span>
              }
              value={processedData.totalEstaciones}
              suffix={`/ ${processedData.totalEstacionesPosibles}`}
              prefix={<TrophyOutlined style={{ color: "#fff" }} />}
              valueStyle={{
                color: "#fff",
                fontSize: "1.5rem",
                fontWeight: "bold",
              }}
            />
            <div style={{ marginTop: "8px" }}>
              <Text style={{ color: "#fff", fontSize: "12px" }}>
                {processedData.totalEstacionesPosibles -
                  processedData.totalEstaciones}{" "}
                pendientes
              </Text>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card
            style={{
              textAlign: "center",
              borderRadius: "16px",
              background: "linear-gradient(135deg, #E91E63 0%, #C2185B 100%)",
              border: "none",
              minHeight: "120px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Statistic
              title={
                <span style={{ color: "#fff", fontSize: "14px" }}>
                  Promedio de Avance
                </span>
              }
              value={processedData.promedioAvance}
              suffix="%"
              prefix={<PercentageOutlined style={{ color: "#fff" }} />}
              valueStyle={{
                color: "#fff",
                fontSize: "2rem",
                fontWeight: "bold",
              }}
            />
            <div style={{ marginTop: "8px" }}>
              <Text style={{ color: "#fff", fontSize: "12px" }}>
                {processedData.usuarios?.filter((u) => u.avanceUser >= 100)
                  .length || 0}{" "}
                completaron 100%
              </Text>
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={[24, 24]}>
        {/* Análisis por departamentos */}
        <Col xs={24}>
          <ChartCard
            title="🏛️ Rendimiento por Departamentos"
            chartType="bar"
            data={{
              labels:
                processedData.barDepartamentoData?.map((item) => item.name) ||
                [],
              datasets: [
                {
                  label: "Participantes Totales",
                  data:
                    processedData.barDepartamentoData?.map(
                      (item) => item.participantes
                    ) || [],
                  backgroundColor: "#0B80C4",
                  borderColor: "#0B80C4",
                  borderWidth: 1,
                },
                {
                  label: "Hombres",
                  data:
                    processedData.barDepartamentoData?.map(
                      (item) => item.hombres
                    ) || [],
                  backgroundColor: "#4CAF50",
                  borderColor: "#4CAF50",
                  borderWidth: 1,
                },
                {
                  label: "Mujeres",
                  data:
                    processedData.barDepartamentoData?.map(
                      (item) => item.mujeres
                    ) || [],
                  backgroundColor: "#E91E63",
                  borderColor: "#E91E63",
                  borderWidth: 1,
                },
                {
                  label: "% Avance Promedio",
                  data:
                    processedData.barDepartamentoData?.map((item) =>
                      parseFloat(item.avance)
                    ) || [],
                  backgroundColor: "#FF9800",
                  borderColor: "#FF9800",
                  borderWidth: 2,
                  type: "line",
                  yAxisID: "y1",
                },
              ],
            }}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  position: "top",
                },
                tooltip: {
                  callbacks: {
                    afterLabel: function (context) {
                      const dataIndex = context.dataIndex;
                      const deptData =
                        processedData.barDepartamentoData[dataIndex];
                      return [
                        `Municipios: ${deptData?.municipios}`,
                        `Densidad: ${deptData?.densidad} part/municipio`,
                        `Completadas: ${deptData?.completadas}/${deptData?.total}`,
                      ];
                    },
                  },
                },
              },
              scales: {
                x: {
                  display: true,
                  title: {
                    display: true,
                    text: "Departamentos",
                  },
                },
                y: {
                  type: "linear",
                  display: true,
                  position: "left",
                  title: {
                    display: true,
                    text: "Número de Participantes",
                  },
                },
                y1: {
                  type: "linear",
                  display: true,
                  position: "right",
                  title: {
                    display: true,
                    text: "Porcentaje de Avance (%)",
                  },
                  min: 0,
                  max: 100,
                  grid: {
                    drawOnChartArea: false,
                  },
                },
              },
            }}
          />
        </Col>
        {/* Distribución por género */}
        <Col xs={24} lg={12}>
          <ChartCard
            title="👥 Distribución por Género"
            chartType="pie"
            showAsPercentage={true}
            data={{
              labels: Object.keys(processedData.pieGeneroData),
              datasets: [
                {
                  label: "Género",
                  data: Object.values(processedData.pieGeneroData),
                  backgroundColor: ["#145BAD", "#5B93D3"],
                },
              ],
            }}
          />
        </Col>

        {/* Distribución por nivel de avance */}
        <Col xs={24} lg={12}>
          <ChartCard
            title="📈 Distribución por Nivel de Avance"
            chartType="pie"
            showAsPercentage={true}
            data={{
              labels: Object.keys(processedData.pieRangosData),
              datasets: [
                {
                  label: "Avance",
                  data: Object.values(processedData.pieRangosData),
                  backgroundColor: [
                    "#F44336",
                    "#FF9800",
                    "#FFC107",
                    "#4CAF50",
                    "#2196F3",
                  ],
                },
              ],
            }}
          />
        </Col>

        {/* Progreso por módulos mejorado */}
        <Col xs={24}>
          <ChartCard
            title="📚 Análisis Detallado por Módulos"
            chartType="bar"
            data={{
              labels:
                processedData.barModuloData?.map((item) => item.name) || [],
              datasets: [
                {
                  label: "Estaciones Completadas",
                  data:
                    processedData.barModuloData?.map(
                      (item) => item.completadas
                    ) || [],
                  backgroundColor: "#4CAF50",
                  borderColor: "#4CAF50",
                  borderWidth: 1,
                },
                {
                  label: "Estaciones Pendientes",
                  data:
                    processedData.barModuloData?.map(
                      (item) => item.pendientes
                    ) || [],
                  backgroundColor: "#FFC107",
                  borderColor: "#FFC107",
                  borderWidth: 1,
                },
                {
                  label: "Participantes",
                  data:
                    processedData.barModuloData?.map(
                      (item) => item.participantes
                    ) || [],
                  backgroundColor: "#0B80C4",
                  borderColor: "#0B80C4",
                  borderWidth: 1,
                  type: "line",
                  yAxisID: "y1",
                },
              ],
            }}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  position: "top",
                },
                tooltip: {
                  callbacks: {
                    afterLabel: function (context) {
                      const dataIndex = context.dataIndex;
                      const moduleData = processedData.barModuloData[dataIndex];
                      return [
                        `Porcentaje Avance: ${moduleData?.porcentaje}%`,
                        `Eficiencia: ${moduleData?.eficiencia}`,
                      ];
                    },
                  },
                },
              },
              scales: {
                x: {
                  display: true,
                  title: {
                    display: true,
                    text: "Módulos",
                  },
                },
                y: {
                  type: "linear",
                  display: true,
                  position: "left",
                  title: {
                    display: true,
                    text: "Estaciones",
                  },
                },
                y1: {
                  type: "linear",
                  display: true,
                  position: "right",
                  title: {
                    display: true,
                    text: "Participantes",
                  },
                  grid: {
                    drawOnChartArea: false,
                  },
                },
              },
            }}
          />
        </Col>

        {/* Tabla de Participantes con Análisis de Riesgo */}
        <Col xs={24}>
          <Card
            title={
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span
                  style={{
                    color: "#0B3360",
                    fontSize: "18px",
                    fontWeight: "bold",
                  }}
                >
                  👥 Análisis Detallado de Participantes con Gestión de Riesgo
                </span>
                <div>
                  <Tag color="red">Sin avance</Tag>
                  <Tag color="orange">Bajo: 25%</Tag>
                  <Tag color="yellow">Medio: 25-50%</Tag>
                  <Tag color="green">Alto: 50%</Tag>
                </div>
              </div>
            }
            style={{ borderRadius: "16px" }}
            extra={
              <Space>
                <Text type="secondary">
                  Total: {processedData.participantesConRiesgo?.length}
                </Text>
                <Button
                  size="small"
                  onClick={exportarExcelDetallado}
                  icon={<ExportOutlined />}
                >
                  Exportar
                </Button>
              </Space>
            }
          >
            <Table
              columns={participantesColumns}
              dataSource={processedData.participantesConRiesgo}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} de ${total} participantes`,
              }}
              scroll={{ x: 1200, y: 600 }}
              size="middle"
              rowClassName={(record) => {
                switch (record.riesgo) {
                  case "Alto":
                    return "risk-high";
                  case "Medio":
                    return "risk-medium";
                  case "Bajo":
                    return "risk-low";
                  default:
                    return "";
                }
              }}
            />
          </Card>
        </Col>
      </Row>

      <style jsx>{`
        .risk-high {
          background-color: #fff2f0;
        }
        .risk-medium {
          background-color: #fff7e6;
        }
        .risk-low {
          background-color: #feffe6;
        }
      `}</style>
    </Card>
  );
};

export default EspigaDashboard;
